import { useState, useCallback, useEffect, useMemo, useRef } from "react";

import { isArray } from "@cayuse/shared-react.utils.type-checks";
import { map } from "lodash";


import { IS_DEV } from "globals/constants";
import { notifyError } from "utils";

import { classificationCodes } from "../../../services/api/classificationCodes";
import styles from "./internal/CodeSearch/styles.css";

const codeTypes = [ "for", "herdc", "seo", "toa" ];
const DEFAULT_PAGE_SIZE = 100;
const startStr = "flw.mapAttr(";
const endStr = ")";

export const mapValueToCodes = (value, minimumNumberOfCodes) => {
	if (value && Array.isArray(value) && value.length >= minimumNumberOfCodes) {
		return value;
	}

	const length = (!value || !Array.isArray(value)) ? minimumNumberOfCodes : minimumNumberOfCodes - value.length;

	return Array.from(
		{ length },
		_ => ({ code: "", percentage: "" }),
	);
};

export const getOptionStyles = level => {
	const levelClassName = level === 0
		? styles.searchListItemZeroLevel
		: level === 1
			? styles.searchListItemFirstLevel
			: level === 2
				? styles.searchListItemSecondLevel
				: "";

	return `${styles.searchListItem} ${levelClassName}`;
};

const flatMapFORCodeOptions = (arr, limitToFourDigits, level = 0) => {
	return arr.reduce((acc, item) => {
		acc.push({
			text: `${item.value} - ${item.description}`,
			value: item.value,
			level,
			maxlevel: 2,
		});

		if ("subCodes" in item) {
			if (limitToFourDigits && level !== 1) {
				acc.push(...flatMapFORCodeOptions(item.subCodes, limitToFourDigits, level + 1));
			} else if (!limitToFourDigits) {
				acc.push(...flatMapFORCodeOptions(item.subCodes, limitToFourDigits, level + 1));
			}
		}

		return acc;
	}, []);
};

export const toBasicCodeOptions = (arr, limitToFourDigits = false) => {
	if (arr.length === 0) {
		return [];
	}

	return flatMapFORCodeOptions(arr, limitToFourDigits)
		.reduce((acc, item) => {
			item.className = getOptionStyles(item.level);
			item.maxlevel = 0;

			if (limitToFourDigits) {
				item.disabled = item.level !== 1;
				item.description = item.level !== 1 && "(select from 4 digit options)";
				item.maxlevel = 1;
			} else {
				item.disabled = item.level !== 2;
				item.description = item.level !== 2 && "(select from 6 digit options)";
				item.maxlevel = 2;
			}

			acc.push(item);

			return acc;
		}, []);
};

export const toTOACodeOptions = arr => {
	if (!isArray(arr) || arr.length === 0) {
		return [];
	}

	return arr.map(item => ({
		className: getOptionStyles(0),
		text: item.type,
		value: item.type,
		level: 0,
		maxlevel: 0,
	}));
};

export const toHERDCCodeOptions = (arr, excludeSubcategories) => {
	if (arr.length === 0) {
		return [];
	}

	return arr.reduce(
		(options, item) =>
			[
				...options,
				toHERDCOption(item.category, 0, excludeSubcategories),
				...toHERDCSubcategoryOptions(item?.subCategories, excludeSubcategories),
			],
		[]
	)
		.map(option => ({
			...option,
			className: getOptionStyles(option.level),
		}));
};

const toHERDCSubcategoryOptions = (subCategories, excludeSubcategories) => {
	if (excludeSubcategories || !isArray(subCategories) || subCategories.length === 0) {
		return [];
	}

	return subCategories.reduce(
		(options, subCategory) => [
			...options,
			toHERDCOption(subCategory, 1, excludeSubcategories),
		],
		[],
	);
};

const toHERDCOption = (category, level = 0, excludeSubcategories) => ({
	text: category,
	value: category,
	maxlevel: excludeSubcategories ? 0 : 1,
	level,
});


export const searchCodes = (codeType, search) => {
	if (!codeTypes.includes(codeType)) {
		throw new Error(`${codeType} is incorrect code`);
	}

	return classificationCodes.findAll({ codeType: codeType, search: search, size: DEFAULT_PAGE_SIZE })
		.then(response => response.content)
		.catch(error => {
			const message = IS_DEV ? error.message : `Search ${codeType} Code: An error has occurred`;
			notifyError(message, error);

			return [];
		});
};

export const useCodeSearchNavigation = ({ value, onChangeCode, options }) => {
	const wrapperRef = useRef(null);
	const optionListRef = useRef(null);
	const focused = useRef(null);
	const focusWasChangedManually = useRef(false);
	const lastEvent = useRef(null);
	const [ isOpen, setIsOpen ] = useState(false);

	const scrollFocusedItemIntoView = useCallback((node = null) => {
		if (!optionListRef.current) {
			return;
		}

		const menu = optionListRef.current;

		const item = node || menu.querySelector("div.item[data-focused='true']");
		if (!item) {
			return;
		}

		const isOutOfUpperView = item.offsetTop < menu.scrollTop;
		const isOutOfLowerView = item.offsetTop + item.clientHeight > menu.scrollTop + menu.clientHeight;

		if (isOutOfUpperView) {
			menu.scrollTop = item.offsetTop;
		} else if (isOutOfLowerView) {
			// eslint-disable-next-line no-mixed-operators
			menu.scrollTop = item.offsetTop + item.clientHeight - menu.clientHeight;
		}
	}, []);

	const onOptionListChange = useCallback(entries => {
		entries.forEach(mutation => {
			if (mutation.target.classList.contains("visible")) {
				scrollFocusedItemIntoView();
			}
		});
	}, [ scrollFocusedItemIntoView ]);

	// use IntersectionObserver to check when menu become visible
	const observer = useRef(new IntersectionObserver(onOptionListChange));

	const setFocused = useCallback((node, changedManually = true) => {
		if (!node) {
			return;
		}

		focusWasChangedManually.current = changedManually;

		node.setAttribute("data-focused", "true");
		focused.current = node;
	}, []);

	const removeFocused = useCallback(() => {
		focusWasChangedManually.current = false;

		if (focused.current) {
			focused.current.setAttribute("data-focused", "false");
			focused.current = null;
		}
	}, []);

	const getSiblingNode = useCallback((node, sibling) => {
		if (!node || !node[sibling]) {
			return;
		}

		const selected = node[sibling].attributes["aria-selected"].value === "true";
		const disabled = node[sibling].attributes["aria-disabled"].value === "true";

		if (selected || disabled) {
			return getSiblingNode(node[sibling], sibling);
		}

		return node[sibling];
	}, []);

	const onMouseEnter = useCallback(event => {
		// check last event is keyboard event to prevent setFocus with mouse when user navigate using keyboard
		if (!(lastEvent.current instanceof KeyboardEvent)) {
			removeFocused();
			setFocused(event.currentTarget);
		}
		lastEvent.current = event;
	}, [ removeFocused, setFocused ]);

	const onKeyDown = event => {
		lastEvent.current = event;

		const optionAnnouncer = wrapperRef?.current?.querySelector("span[aria-relevant='additions text']");

		if (optionAnnouncer) {
			optionAnnouncer.textContent = "";
		}

		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.stopPropagation();

			const availableSiblingNode = getSiblingNode(
				focused.current,
				event.code === "ArrowUp" ? "previousElementSibling" : "nextElementSibling",
			);

			const currentSelectedOptionText = availableSiblingNode?.querySelector("span")?.textContent;

			if (availableSiblingNode) {
				removeFocused();
				setFocused(availableSiblingNode);
				scrollFocusedItemIntoView();

				if (optionAnnouncer && currentSelectedOptionText) {
					optionAnnouncer.textContent = currentSelectedOptionText;
				}
			}
		} else if (event.code === "Enter") {
			// submit focus was changed with keyboard or mouse
			if (focusWasChangedManually.current) {
				onChangeCode(focused.current?.dataset?.value || value);
			}
		}
	};

	const preparedOptions = useMemo(
		() => options.map(item => ({ ...item, onMouseEnter })),
		[ options, onMouseEnter ],
	);

	const setFocusOnOption = useCallback(options => {
		if (options.length === 0) {
			return;
		}

		let option;

		if (value) {
			// find the selected option
			option = optionListRef.current.querySelector(
				`div.item[aria-disabled='true'][aria-selected='true'][data-value='${value}']`
			);
		} else {
			// find the first available option
			option = optionListRef.current.querySelector("div.item[aria-disabled='false'][aria-selected='false']");
		}

		setFocused(option, false);
	}, [ setFocused, value ]);

	useEffect(() => {
		if (!wrapperRef.current) {
			return;
		}

		wrapperRef.current.addEventListener("keydown", onKeyDown, false);

		optionListRef.current = wrapperRef.current.querySelector("div.menu[role='listbox']");

		// assign IntersectionObserver
		observer.current.observe(optionListRef.current);

		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			wrapperRef.current.removeEventListener("keydown", onKeyDown, false);
		};
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, []);

	useEffect(() => {
		// set focus when menu is open and search result is returned
		setFocusOnOption(preparedOptions);
	}, [ preparedOptions ]);

	useEffect(() => {
		if (!isOpen) {
			removeFocused();
		} else {
			// set focus when menu is open
			setFocusOnOption(preparedOptions);
		}
	}, [ isOpen ]);

	useEffect(() => {
		// remove the focused element when the user has removed the selected element
		if (value === "" && focused.current) {
			removeFocused();
		}
	}, [ value, removeFocused ]);

	const selectedText = useMemo(() => {
		const selectedOption = options.find(option => option.value === value);

		return selectedOption?.text ?? value;
	}, [ options, value ]);

	return {
		setIsOpen,
		text: value ? (isOpen ? (focused.current?.dataset?.text ?? selectedText) : selectedText) : "",
		preparedOptions,
		wrapperRef,
	};
};

const getStringPositions = (startStr, endStr, str, position) => {
	const startPosition = str.indexOf(startStr, position);
	if (startPosition === -1) {
		return null;
	}
	const endPosition = str.indexOf(endStr, startPosition);

	return { startPosition, endPosition };
};

export const parseExpression = (formContent, expression) => {
	let position = getStringPositions(startStr, endStr, expression, 0);
	while (position) {
		let codes = null;
		const subString = expression.substring(position.startPosition + startStr.length, position.endPosition);
		const replaceString = expression.substring(position.startPosition, position.endPosition + 1);
		const items = subString.split(", ");

		map(formContent[items[0]], items[1]?.replaceAll("'", ""))
			.forEach(code => codes = codes ? `${codes}, '${code}'` : `'${code}'`);
		expression = expression.replace(replaceString, `${codes}`);
		position = getStringPositions(startStr, endStr, expression, position.endPosition);
	}

	return expression;
};